import { useRef, useState } from "react";
import { Link, createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { auth } from "../../lib/authManager";
import { isLocalMode, isSearchFlag } from "../../lib/appMode";
import {
  describeAuthError,
  errorsOnBlur,
  errorsOnSubmit,
  errorsOnType,
  firstErrorField,
  nextTarget,
  signInChecks,
  signInErrors,
  type AuthErrorInfo,
  type ShownErrors,
} from "../../lib/authForm";
import { authEmailDraft, forgetAuthEmail, rememberAuthEmail } from "../../lib/authEmail";
import { Spinner } from "../../components/ui";
import { AuthBody, AuthField, AuthFooter, AuthHeading, AuthPending } from "../../components/auth-card";
import { ArrowRightIcon } from "../../components/icons";
import { LocalSignIn } from "../../components/local-signin";
import { PasswordInput } from "../../components/password-input";

export const Route = createFileRoute("/auth/signin")({
  // `cancelled`: back from the hosted approval page's Cancel (local mode's
  // redirect flow). The router JSON-parses search values: `1` is a number,
  // so a `next` that isn't a string is dropped rather than failing the page.
  validateSearch: z.object({
    next: z.string().optional().catch(undefined),
    cancelled: z.union([z.string(), z.number(), z.boolean()]).optional(),
  }),
  // Already signed in: straight on to `next`, as a finished sign-in would
  // go — the native apps open this page in the system sheet, which shares
  // Safari's session, with next=/link?code=…&redirect=1; sent to Home
  // instead, the consent card never showed and the app waited on its pin.
  beforeLoad: ({ search }) => {
    if (auth.getState().status === "signedIn") throw redirect(nextTarget(search.next));
  },
  component: SignInPage,
  pendingComponent: AuthPending,
});

/** The server-hosted app signs in through the hosted app (a link pin +
 * redirect, Plex style) — no password form on a plain-http LAN page. */
function SignInPage() {
  const { next, cancelled } = Route.useSearch();
  return isLocalMode ? (
    <LocalSignIn next={next} cancelled={isSearchFlag(cancelled)} />
  ) : (
    <SignIn />
  );
}

type SignInField = "email" | "password";

function SignIn() {
  const navigate = useNavigate();
  const { next } = Route.useSearch();
  const [email, setEmail] = useState(authEmailDraft);
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<ShownErrors<SignInField>>({});
  // What the API said: under the password, for either field's typing to clear.
  const [failure, setFailure] = useState<AuthErrorInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const refs = {
    email: useRef<HTMLInputElement>(null),
    password: useRef<HTMLInputElement>(null),
  };

  function edit(field: SignInField, value: string) {
    if (field === "email") {
      setEmail(value);
      rememberAuthEmail(value);
    } else {
      setPassword(value);
    }
    setErrors((prev) => errorsOnType(prev, field, signInChecks[field](value)));
    setFailure(null);
  }

  function leave(field: SignInField) {
    const value = field === "email" ? email : password;
    setErrors((prev) => errorsOnBlur(prev, field, value, signInChecks[field](value)));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const found = signInErrors(email, password);
    const first = firstErrorField(found);
    setErrors(errorsOnSubmit(found));
    setFailure(null);
    if (first) {
      refs[first].current?.focus();
      return;
    }
    setBusy(true);
    try {
      await auth.signIn(email.trim(), password);
      forgetAuthEmail();
      await navigate(nextTarget(next));
    } catch (err) {
      setFailure(describeAuthError(err, "signin"));
      setBusy(false);
    }
  }

  const passwordError = errors.password?.message ?? failure?.message;
  const passwordInvalid = Boolean(errors.password) || failure?.invalid === true;

  return (
    <>
      <AuthBody>
        <AuthHeading title="Welcome back">Sign in to your Rago account.</AuthHeading>
        <form onSubmit={submit} noValidate className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <AuthField id="email" label="Email" error={errors.email?.message} alert={errors.email?.alert}>
              <input
                ref={refs.email}
                id="email"
                type="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                autoFocus={!email}
                className="rago-input rago-input-lg"
                value={email}
                onChange={(e) => edit("email", e.target.value)}
                onBlur={() => leave("email")}
                aria-invalid={errors.email ? true : undefined}
                aria-describedby={errors.email ? "email-error" : undefined}
              />
            </AuthField>
            <AuthField
              id="password"
              label="Password"
              error={passwordError}
              alert={errors.password ? errors.password.alert : true}
              aside={
                <Link
                  to="/auth/forgot"
                  search={{ next }}
                  className="text-[13.5px] font-medium text-[var(--rago-accent)] transition-colors hover:text-[var(--rago-accent-strong)]"
                >
                  Forgot?
                </Link>
              }
            >
              <PasswordInput
                ref={refs.password}
                id="password"
                size="lg"
                autoComplete="current-password"
                autoFocus={Boolean(email)}
                value={password}
                onChange={(v) => edit("password", v)}
                onBlur={() => leave("password")}
                invalid={passwordInvalid}
                describedBy={passwordError ? "password-error" : undefined}
              />
            </AuthField>
          </div>
          <button type="submit" className="rago-btn rago-btn-block rago-btn-lg" disabled={busy}>
            {busy ? <Spinner /> : null}
            Sign in
          </button>
        </form>
      </AuthBody>
      <AuthFooter prompt="New to Rago?">
        <Link to="/auth/signup" search={{ next }} className="rago-auth-switch">
          Create an account
          <ArrowRightIcon size={13} bold />
        </Link>
      </AuthFooter>
    </>
  );
}
